import React from 'react';
import { TemplateDataModel } from '@/component/Template/TemplateDataModel';
import { serviceQuickBiReportTemplate } from '@/component/Template/services/template';
import { isError } from '@/component/common/error';
import { requestLoading } from '@/hooks/hooksPc/useRequestLoadingPc';

// 文件顶部新增：模块级单例缓存容器
const templateCacheSingleton = new Map<string, TemplateDataModel>();
const inflightSingleton = new Map<string, Promise<TemplateDataModel>>();

export function useQuickBiTemplate(reportSlug?: string, templateCode?: string) {
  const [template, setTemplate] = React.useState<TemplateDataModel>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // 通过 ref 引用单例，避免每次 hook 重新创建 Map
  const templateCache = React.useRef(templateCacheSingleton).current;
  const inflightMap = React.useRef(inflightSingleton).current;

  const fetchTemplate = React.useCallback(async () => {
    if (!reportSlug || !templateCode) return;

    const key = `${reportSlug}::${templateCode}`;

    // 命中缓存：直接返回，不触发网络请求
    const cached = templateCache.get(key);
    if (cached) {
      setTemplate(cached);
      setLoading(false);
      setError(null);
      return;
    }

    // 命中进行中的请求：复用 Promise，避免并发重复
    const inflight = inflightMap.get(key);
    if (inflight) {
      setLoading(true);
      try {
        const data = await inflight;
        setTemplate(data);
        setError(null);
      } catch {
        setError('获取模板失败');
      } finally {
        setLoading(false);
      }
      return;
    }

    // 发起新请求，并写入并发复用表
    const reqPromise = (async () => {
      const res = await requestLoading(
        serviceQuickBiReportTemplate({ report: reportSlug, template_code: templateCode })
      );
      if (isError(res)) {
        throw new Error('获取模板失败');
      }
      return res.data;
    })();

    inflightMap.set(key, reqPromise);
    setLoading(true);
    setError(null);

    try {
      const data = await reqPromise;
      templateCache.set(key, data); // 写入缓存
      setTemplate(data);
    } catch (e) {
      setError('获取模板失败');
    } finally {
      inflightMap.delete(key); // 清理并发表
      setLoading(false);
    }
  }, [reportSlug, templateCode]);

  React.useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  return { template, loading, error, refresh: fetchTemplate };
}

export default useQuickBiTemplate;